import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { DEFAULT_LANGUAGE } from '@/constants';
import Text from '../Text';
import Icon from '../Icon';
import DropdownOptionsData from '../Dropdown/DropdownOptionsData';

function SelectInput({
  isDisable,
  value = null,
  onSelectHandler,
  placeholder = '',
  label = 'Phone',
  isRequired = false,
  name,
  options = [],
}) {
  const { lang: selectLang } = useSelector((state) => state.globalSettings);

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value ?? null);
  const [error, setError] = useState('');
  const selectRef = useRef(null);

  // console.log({ name, options });

  useEffect(() => {
    if (value) {
      setSelectedOption(value);
    }
  }, [value]);

  const handleToggleDropdown = () => {
    if (!isDisable) {
      setShowDropdown((prevState) => !prevState);
    }
  };

  const handleCloseDropdown = () => {
    setShowDropdown(false);
  };

  const handleSelectOption = (data) => {
    setSelectedOption(data);
    onSelectHandler({
      target: {
        value: data,
        name,
      },
    });
  };

  // TODO: will use this to get the format label
  const getLabel = (data) => {};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showDropdown &&
        selectRef.current &&
        !selectRef.current.contains(event.target)
      ) {
        handleCloseDropdown();
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div
      ref={selectRef}
      className={cn('select-input relative w-full', {
        'pointer-events-none': isDisable,
      })}
      role="presentation"
      onClick={handleToggleDropdown}
    >
      <div
        className={cn(
          'flex flex-row items-center h-14 justify-center pl-16 pr-12 border border-senate rounded-[4px] overflow-hidden font-encode-sans focus-within:border-2 active:border-2 transition-all duration-200',
          {
            '!border-infrared-burn': error,
            'text-atelier placeholder:text-atelier border-atelier': isDisable,
          },
        )}
      >
        <div className="flex items-center cursor-pointer" />

        <input
          type="select"
          name={name}
          className={cn(
            'text-gray-dark block w-full px-0 py-16 text-16 focus:outline-none focus:ring-0 placeholder:text-senate font-encode-sans !placeholder:text-16 placeholder:font-encode-sans placeholder:font-normal',
            {
              '!text-atelier': isDisable,
            },
          )}
          placeholder={placeholder}
          value={
            selectedOption?.name?.[selectLang] ??
            selectedOption?.name[DEFAULT_LANGUAGE]
          }
          disabled={isDisable}
          readOnly
        />

        <div className="min-w-[18px] hover:cursor-pointer">
          <Icon
            type="angle-down"
            width="20"
            height="20"
            color="#49525E"
            className={classNames('transition-all duration-200', {
              'rotate-180': showDropdown,
            })}
          />
        </div>
      </div>

      <label
        className={classNames(
          'absolute top-[17px] font-encode-sans left-3 text-14 font-medium text-senate leading-16 tracking-[0.24px] bg-white duration-300 transform -translate-y-6 px-[6px]',
          {
            '!text-infrared-burn': error,
          },
        )}
      >
        {label}
        {isRequired ? (
          <Text type="span" className="!text-infrared-burn">
            *
          </Text>
        ) : null}
      </label>

      {error ? (
        <Text
          type="span"
          className="font-encode-sans text-14 text-infrared-burn font-medium leading-4 track-[0.24px] px-16"
        >
          {error}
        </Text>
      ) : null}

      {/* dropdown selection */}
      <DropdownOptionsData
        optionValue={selectedOption}
        onSelectOption={handleSelectOption}
        onView={showDropdown}
        onClose={handleCloseDropdown}
        name={name}
        options={options || []}
        inputSelectionRef={selectRef}
      />
    </div>
  );
}

export default SelectInput;
